import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const DAYS = ["L", "M", "M", "J", "V", "S", "D"];

function fmtFR(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR",
    { day: "numeric", month: "long", year: "numeric" });
}

function isoToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function daysBetween(d1, d2) {
  const a = new Date(d1 + "T00:00:00");
  const b = new Date(d2 + "T00:00:00");
  return Math.floor((b - a) / (24 * 3600 * 1000)) + 1;
}

export default function ReserverSalle() {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [details, setDetails] = useState([]); // {date, statut}
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [form, setForm] = useState({
    nom: user?.nom || "",
    prenom: user?.prenom || "",
    email: user?.email || "",
    telephone: "",
    adresse: "",
    type_evenement: "",
    nb_personnes: "",
    message: "",
    commune: "",
    type_tarif: "particulier_hors",
    heure_debut: "14:00",
    heure_fin: "18:00",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get("/salle/reservations/occupees")
      .then((d) => setDetails(d.details || []))
      .catch(() => setDetails([]));
  }, [success]);

  // Map date -> statut pour lookup rapide
  const statutByDate = useMemo(() => {
    const m = {};
    details.forEach((d) => { m[d.date] = d.statut; });
    return m;
  }, [details]);

  // Génère les cases du mois
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startDow = (first.getDay() + 6) % 7; // lundi = 0
    const total = last.getDate();
    const arr = [];
    for (let i = 0; i < startDow; i++) arr.push(null);
    for (let d = 1; d <= total; d++) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      arr.push(iso);
    }
    return arr;
  }, [year, month]);

  const today = isoToday();

  const isInRange = (iso) => {
    if (!dateDebut) return false;
    if (!dateFin) return iso === dateDebut;
    return iso >= dateDebut && iso <= dateFin;
  };

  const onClickDay = (iso) => {
    if (!iso || iso < today) return;
    if (statutByDate[iso]) return; // pris
    if (!dateDebut || (dateDebut && dateFin)) {
      setDateDebut(iso); setDateFin(""); setError("");
    } else {
      // 2e clic : définit la fin
      if (iso < dateDebut) {
        setDateDebut(iso); setDateFin("");
      } else {
        // Vérifier qu'il n'y a pas de jour pris entre les deux
        const d1 = new Date(dateDebut + "T00:00:00");
        const d2 = new Date(iso + "T00:00:00");
        let cur = new Date(d1);
        while (cur <= d2) {
          const isoCur = cur.toISOString().slice(0, 10);
          if (statutByDate[isoCur]) {
            setError("Cette plage contient des jours déjà réservés.");
            return;
          }
          cur.setDate(cur.getDate() + 1);
        }
        setDateFin(iso); setError("");
      }
    }
  };

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const submit = async () => {
    setError("");
    if (!dateDebut) { setError("Sélectionnez au moins une date sur le calendrier."); return; }
    if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) {
      setError("Nom, prénom et email sont obligatoires."); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        nom: form.nom.trim(),
        prenom: form.prenom.trim(),
        email: form.email.trim(),
        date_debut: dateDebut,
        date_fin: dateFin || dateDebut,
        nb_personnes: parseInt(form.nb_personnes) || 0,
      };
      const r = await api.post("/salle/reservations", payload);
      setSuccess(r);
    } catch (e) {
      setError(e.message || "Erreur lors de l'envoi.");
    } finally { setBusy(false); }
  };

  if (success) {
    return (
      <div className="container">
        <h1>Demande envoyée</h1>
        <div className="card success-card">
          <h2 style={{ marginTop: 0 }}>✓ Votre demande a bien été enregistrée</h2>
          <p>{success.message}</p>
          <p className="muted">Numéro de demande : <strong>#{success.id}</strong></p>
          <div style={{ marginTop: "1rem", display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <Link to={`/contrat-salle/${success.id}`} className="hero-v2-cta-primary"
                  style={{ background: "var(--gold)", color: "#2d1b00",
                           padding: "0.7rem 1.4rem", borderRadius: 6,
                           textDecoration: "none", fontWeight: 600 }}>
              📄 Télécharger le contrat pré-rempli
            </Link>
            <Link to={`/etat-des-lieux/${success.id}`}
                  style={{ padding: "0.7rem 1.4rem", borderRadius: 6,
                           background: "#fff", color: "var(--accent)",
                           border: "1.5px solid var(--accent)",
                           textDecoration: "none", fontWeight: 500 }}>
              📋 Télécharger l'état des lieux
            </Link>
            <Link to="/rubrique/salle-des-fetes" className="secondary"
                  style={{ padding: "0.7rem 1.2rem",
                           border: "1px solid rgba(0,0,0,0.15)", borderRadius: 6,
                           textDecoration: "none", display: "inline-block" }}>
              ← Retour à la page Salle communale
            </Link>
          </div>
          <p className="muted" style={{ fontSize: "0.85em", marginTop: "1rem" }}>
            Vous pourrez imprimer ce contrat ou le sauvegarder en PDF
            (Ctrl+P → "Enregistrer au format PDF") afin de le déposer signé à la mairie
            avec votre attestation d'assurance.
          </p>
        </div>
      </div>
    );
  }

  const nbJours = dateDebut && dateFin ? daysBetween(dateDebut, dateFin) :
                   dateDebut ? 1 : 0;

  return (
    <div className="container">
      <h1>Pré-réservation de la salle communale</h1>
      <p className="muted">
        Sélectionnez vos dates sur le calendrier puis remplissez le formulaire.
        Votre demande sera examinée par la mairie qui vous contactera pour confirmer.
      </p>

      <div className="resa-layout">
        {/* Calendrier */}
        <div className="card">
          <div className="resa-cal-header">
            <button className="small secondary" onClick={goPrev}>← Précédent</button>
            <h3>{MONTHS[month]} {year}</h3>
            <button className="small secondary" onClick={goNext}>Suivant →</button>
          </div>

          <div className="resa-cal-grid">
            {DAYS.map((d, i) => (
              <div key={i} className="resa-cal-dayhead">{d}</div>
            ))}
            {cells.map((iso, i) => {
              if (!iso) return <div key={i} className="resa-cal-cell empty" />;
              const past = iso < today;
              const statut = statutByDate[iso];
              const inRange = isInRange(iso);
              let cls = "resa-cal-cell";
              if (past) cls += " past";
              else if (statut === "validee") cls += " validee";
              else if (statut === "en_attente") cls += " enattente";
              else if (inRange) cls += " selected";
              return (
                <button key={i} className={cls}
                        disabled={past || !!statut}
                        onClick={() => onClickDay(iso)}>
                  {parseInt(iso.slice(-2))}
                </button>
              );
            })}
          </div>

          <div className="resa-legend">
            <span><span className="dot validee" /> Réservé (validé)</span>
            <span><span className="dot enattente" /> En attente</span>
            <span><span className="dot selected" /> Votre sélection</span>
            <span><span className="dot past" /> Date passée</span>
          </div>
          <p className="muted" style={{ fontSize: "0.85em", marginTop: "0.5rem" }}>
            Cliquez sur un jour pour commencer, puis sur un autre pour étendre la
            sélection sur plusieurs jours consécutifs.
          </p>
        </div>

        {/* Formulaire */}
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Vos coordonnées</h3>

          {dateDebut && (
            <div className="resa-summary">
              <strong>Dates choisies :</strong><br/>
              {fmtFR(dateDebut)}
              {dateFin && dateFin !== dateDebut && <> → {fmtFR(dateFin)}</>}
              <div className="muted" style={{ fontSize: "0.85em" }}>
                {nbJours} jour{nbJours > 1 ? "s" : ""}
              </div>
              <button className="small secondary" style={{ marginTop: 6 }}
                      onClick={() => { setDateDebut(""); setDateFin(""); }}>
                Réinitialiser
              </button>
            </div>
          )}

          <div className="field-row">
            <div className="field"><label>Prénom *</label>
              <input value={form.prenom}
                     onChange={(e) => setForm({ ...form, prenom: e.target.value })} /></div>
            <div className="field"><label>Nom *</label>
              <input value={form.nom}
                     onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
          </div>
          <div className="field-row">
            <div className="field"><label>Email *</label>
              <input type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="field"><label>Téléphone</label>
              <input type="tel" value={form.telephone}
                     onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></div>
          </div>
          <div className="field">
            <label>Adresse complète</label>
            <input value={form.adresse}
                   onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                   placeholder="N°, rue, code postal, ville" />
          </div>
          <div className="field">
            <label>Commune de résidence</label>
            <input value={form.commune}
                   onChange={(e) => setForm({ ...form, commune: e.target.value })}
                   placeholder="ex : La Chapelle-Saint-Fray ou autre" />
          </div>

          <div className="field-row">
            <div className="field"><label>Type d'événement</label>
              <input value={form.type_evenement}
                     onChange={(e) => setForm({ ...form, type_evenement: e.target.value })}
                     placeholder="ex : Anniversaire, mariage, repas de famille…" /></div>
            <div className="field"><label>Nombre d'invités</label>
              <input type="number" min="0" max="60" value={form.nb_personnes}
                     onChange={(e) => setForm({ ...form, nb_personnes: e.target.value })} /></div>
          </div>

          <div className="field-row">
            <div className="field"><label>Heure d'arrivée souhaitée</label>
              <input type="time" value={form.heure_debut}
                     onChange={(e) => setForm({ ...form, heure_debut: e.target.value })} /></div>
            <div className="field"><label>Heure de départ</label>
              <input type="time" value={form.heure_fin}
                     onChange={(e) => setForm({ ...form, heure_fin: e.target.value })} /></div>
          </div>

          <div className="field">
            <label>Tarif applicable</label>
            <select value={form.type_tarif}
                    onChange={(e) => setForm({ ...form, type_tarif: e.target.value })}>
              <option value="particulier_commune">Particulier - Commune (300 €/week-end)</option>
              <option value="particulier_hors">Particulier - Hors commune (400 €/week-end)</option>
              <option value="association_commune">Association - Commune (70 €/week-end)</option>
              <option value="association_hors">Association - Hors commune (100 €/week-end)</option>
            </select>
            <p className="muted" style={{ fontSize: "0.8em", marginTop: 4 }}>
              Le tarif sera confirmé par la mairie selon votre situation.
            </p>
          </div>

          <div className="field"><label>Message complémentaire</label>
            <textarea rows={4} value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Précisions sur votre événement, horaires, etc." />
          </div>

          {error && <div className="error">{error}</div>}

          <div style={{ marginTop: "1rem", display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <button onClick={submit} disabled={busy || !dateDebut}>
              {busy ? "Envoi…" : "Envoyer ma demande"}
            </button>
            <Link to="/rubrique/salle-des-fetes" className="secondary"
                  style={{ display: "inline-block", padding: "0.55rem 1rem",
                           borderRadius: 6, textDecoration: "none",
                           border: "1px solid rgba(0,0,0,0.15)" }}>
              Annuler
            </Link>
          </div>

          <p className="muted" style={{ fontSize: "0.8em", marginTop: "1rem" }}>
            * Champs obligatoires. La salle peut accueillir jusqu'à 60 personnes.
            Tarif hors commune : 400 € le week-end + cautions (1500 € salle + 400 € ménage).
          </p>
        </div>
      </div>
    </div>
  );
}
